import React, { useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  DashboardOutlined, 
  UserOutlined, 
  CrownOutlined, 
  SettingOutlined, 
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TagsOutlined,
  DeleteOutlined,
  GiftOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { Layout, Menu } from 'antd';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const { Header, Sider, Content } = Layout;

const AdminDashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, adminLogout } = useAdminAuth();

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const menuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/admin/dashboard')
    },
    {
      key: '/admin/users',
      icon: <UserOutlined />,
      label: 'User Management',
      onClick: () => navigate('/admin/users')
    },
    {
      key: '/admin/subscriptions',
      icon: <CrownOutlined />,
      label: 'Subscriptions',
      onClick: () => navigate('/admin/subscriptions')
    },
    {
      key: '/admin/categories',
      icon: <TagsOutlined />,
      label: 'Categories',
      onClick: () => navigate('/admin/categories')
    },
    {
      key: '/admin/promo-codes',
      icon: <GiftOutlined />,
      label: 'Promo Codes',
      onClick: () => navigate('/admin/promo-codes')
    },
    {
      key: '/admin/deleted-users',
      icon: <DeleteOutlined />,
      label: 'Deleted Users',
      onClick: () => navigate('/admin/deleted-users')
    },
    {
      key: 'app-data',
      icon: <DatabaseOutlined />,
      label: 'App Data',
      children: [
        {
          key: '/admin/contacts',
          icon: <MessageOutlined />,
          label: 'Contacts',
          onClick: () => navigate('/admin/contacts')
        },
      ]
    },
    {
      key: '/admin/audit-logs',
      icon: <FileTextOutlined />,
      label: 'Audit Logs',
      onClick: () => navigate('/admin/audit-logs')
    },
    {
      key: '/admin/legal-content',
      icon: <SafetyOutlined />,
      label: 'Legal Content',
      onClick: () => navigate('/admin/legal-content')
    },
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/admin/settings')
    }
  ];

  return (
    <Layout className="min-h-screen">
      {/* Sidebar */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="bg-gradient-to-b from-cyan-400 to-cyan-600"
        width={250}
        style={{ background: 'linear-gradient(to bottom, #22d3ee, #0891b2)' }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-cyan-500/30">
          <img 
            src={`${process.env.PUBLIC_URL}/images/logo.png`}
            alt="GUFFPUFF" 
            className={`transition-all ${collapsed ? 'h-10' : 'h-14'}`}
          />
        </div>

        {/* Menu */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={location.pathname.startsWith('/admin/contacts') ? ['app-data'] : []}
          items={menuItems.filter((item, index, list) => list.findIndex((entry) => entry.key === item.key) === index)}
          className="border-r-0"
          style={{ 
            background: 'transparent',
          }}
        />
        <style jsx>{`
          .ant-menu-dark.ant-menu-inline .ant-menu-item-selected {
            background-color: #E91E8C !important;
            border-left: 4px solid #C0176F;
          }
          .ant-menu-dark.ant-menu-inline .ant-menu-item-selected::after {
            border-right: none;
          }
          .ant-menu-dark .ant-menu-item:hover {
            background-color: rgba(233, 30, 140, 0.2) !important;
          }
        `}</style>
      </Sider>

      {/* Main Layout */}
      <Layout>
        {/* Header */}
        <Header className="bg-white shadow-sm px-4 flex items-center justify-between">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-10 h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-all border-0 outline-none cursor-pointer"
          >
            {collapsed ? <MenuUnfoldOutlined className="text-lg" /> : <MenuFoldOutlined className="text-lg" />}
          </button>

          <div className="flex items-center gap-4">
            {/* Admin Info */}
            <div className="text-right hidden md:block">
              <p className="text-sm font-semibold text-gray-800">{admin?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{admin?.role}</p>
            </div>

            {/* Admin Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center text-white font-bold">
              {admin?.name?.charAt(0).toUpperCase()}
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-all border-0 outline-none cursor-pointer flex items-center gap-2"
            >
              <LogoutOutlined />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </Header>

        {/* Content */}
        <Content className="m-6 p-6 bg-white rounded-lg shadow-sm">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminDashboardLayout;
